import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { find, length, isEmpty, last } from 'ramda';
import { isBlank, Debounce } from 'utils/helperMethods';
import useScreen from 'hooks/useScreen';
import useQuiz from 'reducers/hooks/useQuiz';
import { calculateQuizValues } from 'utils/quizHelper';
import QuizQuestionPresenter, { compareQuestionValues } from 'presenters/QuizQuestionPresenter';
import QuizPresenter from 'presenters/QuizPresenter';
import { quiz } from 'lib/surveys';

import DesktopQuiz from './components/DesktopQuiz';
import MobileQuiz from './components/MobileQuiz';

const INITIAL_CALCULATED_VALUES = {
  mortgagePaymentFinal: 0,
  houseValueDp: 0,
  houseValue: 0,
};

const Quiz = props => {
  const { onFinish } = props;
  const { isDesktop } = useScreen();
  const { questions } = quiz;
  const {
    submitCurrentStep,
    lastQuestionId,
    changeValue,
    quizValues,
    isCurrentQuestionValid,
    setCurrentQuestionValidity,
    manuallyChangedFields,
    setManuallyChangedField,
  } = useQuiz();

  const [calculatedValues, setCalculatedValues] = useState(INITIAL_CALCULATED_VALUES);
  const debouncedCalculation = Debounce(() => {
    setCalculatedValues(calculateQuizValues(quizValues));
  });

  useEffect(() => {
    debouncedCalculation();
  }, [quizValues]);

  const prepareCurrentQuestion = () => {
    const filteredQuestionsByDepending = questions.filter(({ depends }) => {
      if (isBlank(depends)) return true;
      return depends.every(({ field, fieldValue, rule }) => {
        const currentValue = QuizPresenter[field](quizValues);
        return compareQuestionValues(currentValue, fieldValue, rule);
      });
    });

    const currentQuestion = find(({ questionId }) => {
      return questionId > lastQuestionId;
    })(filteredQuestionsByDepending);

    const currentQuestionId = QuizQuestionPresenter.questionId(currentQuestion);
    const lastQuestion = last(filteredQuestionsByDepending);
    const lastQustionId = QuizQuestionPresenter.questionId(lastQuestion);
    const isLastQuestion = lastQustionId === currentQuestionId;
    const answeredQuestions = filteredQuestionsByDepending.filter(({ questionId }) => questionId < currentQuestionId);
    const hasPreviousQuestion = !isEmpty(answeredQuestions);

    const getPreviouseQuestionId = () => {
      if (length(answeredQuestions) < 2) return 0;
      const previousQuestion = answeredQuestions[length(answeredQuestions) - 2];
      return QuizQuestionPresenter.questionId(previousQuestion);
    };

    const previousAnsweredQuestionId = getPreviouseQuestionId();

    return {
      currentQuestion,
      currentQuestionId,
      answeredQuestions,
      previousAnsweredQuestionId,
      hasPreviousQuestion,
      isLastQuestion,
    };
  };

  const handleFinish = () => {
    onFinish();
  };

  const {
    currentQuestion,
    currentQuestionId,
    answeredQuestions,
    previousAnsweredQuestionId,
    hasPreviousQuestion,
    isLastQuestion,
  } = prepareCurrentQuestion();

  const handleNextQuestion = () => {
    submitCurrentStep(currentQuestionId);
  };

  const handleOnPrevious = () => {
    submitCurrentStep(previousAnsweredQuestionId);
  };

  const QuizComponent = isDesktop ? DesktopQuiz : MobileQuiz;

  return (
    <QuizComponent
      currentQuestion={currentQuestion}
      quizValues={quizValues}
      onNext={handleNextQuestion}
      onChange={changeValue}
      isCurrentQuestionValid={isCurrentQuestionValid}
      setCurrentQuestionValidity={setCurrentQuestionValidity}
      manuallyChangedFields={manuallyChangedFields}
      setManuallyChangedField={setManuallyChangedField}
      answeredQuestions={answeredQuestions}
      calculatedValues={calculatedValues}
      onPrevious={handleOnPrevious}
      hasPreviousQuestion={hasPreviousQuestion}
      onFinish={handleFinish}
      isLastQuestion={isLastQuestion}
    />
  );
};

Quiz.propTypes = {
  onFinish: PropTypes.func.isRequired,
};

export default Quiz;



// WEBPACK FOOTER //
// ./src/sharedComponents/Quiz/Quiz.js